import { inject, Injectable, RESPONSE_INIT } from '@angular/core';

/**
 * Sets the HTTP status of a server-rendered response from inside the app.
 *
 * Why this exists: a show URL whose slug no longer resolves used to render the
 * "Show Details" empty state and return **200 OK**. To a crawler that is a soft
 * 404 — the page stays in the index, competes with the real pages, and Search
 * Console reports it as a quality problem rather than a removed show. Returning
 * a real 404 is what gets a cancelled show dropped cleanly.
 *
 * `RESPONSE_INIT` is the object the SSR engine passes to `new Response(...)`
 * after rendering finishes, so mutating it during render is what changes the
 * status. The token is `null` in every non-SSR context — the browser, the
 * prerender pass, route extraction — which is why every call is a no-op there
 * rather than a guard at each call site.
 */
@Injectable({ providedIn: 'root' })
export class SsrResponseService {
  private readonly responseInit = inject(RESPONSE_INIT, { optional: true });

  /**
   * @returns true if the status was applied (i.e. we are server-rendering).
   */
  setStatus(status: number): boolean {
    if (!this.responseInit) {
      return false;
    }
    this.responseInit.status = status;
    return true;
  }

  /** Convenience for the common case. */
  setNotFound(): boolean {
    return this.setStatus(404);
  }
}
